import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';

/**
 * Every captured state of one module, as a strip of thumbnails that open
 * full size.
 *
 * The manual used to carry a single screenshot per module — the list view,
 * and nothing else. That is the one screen a reader can already picture;
 * what they cannot picture is the drawer that opens behind "Add customer",
 * what the delete confirmation actually asks, or which tabs a report has.
 * Those are the states people get stuck in, so those are the states worth
 * showing.
 *
 * The thumbnails are deliberately small and uniform. At this size nobody
 * reads a screenshot — they scan for the one that looks like where they are
 * stuck, then open it. Making them large enough to read would push the
 * written manual off the page, which is the wrong trade: the prose is the
 * documentation, the images are the index into it.
 */
const ScreenGallery = ({ screens = [], title, variant = 'desktop' }) => {
  /* 'phone' wraps every shot in a device bezel and switches the tile from
     16:10 to the handset's own 9:19.5. A phone screenshot letterboxed into
     a landscape tile is mostly empty tile, and without a frame a tall
     screenshot on a white page reads as a floating column of UI rather
     than as a phone. */
  const isPhone = variant === 'phone';
  const [open, setOpen] = useState(null);

  const step = useCallback(
    delta => setOpen(i => (i === null ? null : (i + delta + screens.length) % screens.length)),
    [screens.length],
  );

  /* Arrow keys and Escape are what people reach for in a viewer, so they
     work; the listener only exists while one is open. */
  useEffect(() => {
    if (open === null) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') setOpen(null);
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, step]);

  if (!screens.length) return null;
  const current = open === null ? null : screens[open];

  return (
    <>
      <div className={`screen-strip ${isPhone ? 'screen-strip--phone' : ''}`}>
        {screens.map((s, i) => (
          <button
            key={s.src}
            type="button"
            onClick={() => setOpen(i)}
            className={`screen-thumb focus-ring ${isPhone ? 'screen-thumb--phone' : ''}`}
            aria-label={`View ${s.label}${title ? ` — ${title}` : ''}`}
          >
            <span className="screen-thumb__shot">
              {isPhone && <span className="screen-thumb__notch" aria-hidden="true" />}
              <img src={s.src} alt="" loading="lazy" width="1440" height="900" />
              <Maximize2 className="screen-thumb__zoom" aria-hidden="true" />
            </span>
            <span className="screen-thumb__label">{s.label}</span>
          </button>
        ))}
      </div>

      {current && (
        <div
          className={`screen-viewer ${isPhone ? 'screen-viewer--phone' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label={current.label}
          onClick={() => setOpen(null)}
        >
          {/* Stop propagation on the frame so a click on the image itself
              does not close what the reader just opened. */}
          <figure className="screen-viewer__frame" onClick={e => e.stopPropagation()}>
            <img src={current.src} alt={current.label} />
            <figcaption className="screen-viewer__cap">
              <span>
                <strong>{current.label}</strong>
                {current.note ? <span className="screen-viewer__note">{current.note}</span> : null}
              </span>
              <span className="screen-viewer__count">
                {open + 1} / {screens.length}
              </span>
            </figcaption>
          </figure>

          {screens.length > 1 && (
            <>
              <button
                type="button"
                className="screen-viewer__nav screen-viewer__nav--prev focus-ring"
                onClick={e => { e.stopPropagation(); step(-1); }}
                aria-label="Previous screen"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                className="screen-viewer__nav screen-viewer__nav--next focus-ring"
                onClick={e => { e.stopPropagation(); step(1); }}
                aria-label="Next screen"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}

          <button
            type="button"
            className="screen-viewer__close focus-ring"
            onClick={() => setOpen(null)}
            aria-label="Close viewer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
    </>
  );
};

export default ScreenGallery;
